import {
  useTranslation,
  type CheckoutExecution,
  type PaymentInteractionOutcome,
} from '@checkout/core'
import {
  Button,
  StripeExpressCheckoutError,
  StripeExpressCheckoutSection,
  type StripeExpressCheckoutSectionHandle,
} from '@checkout/ui'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'

import type { CheckoutPresentationRunnerHandle, CheckoutPresentationRunnerProps } from './types'

function stripeFailureOutcome(
  methodId: string,
  error: StripeExpressCheckoutError
): PaymentInteractionOutcome {
  return {
    status: 'failed',
    provider: 'stripe',
    pmId: methodId,
    error: error.message,
    errorCode: error.code,
    raw: error.raw,
  }
}

function nextActionClientSecret(execution: CheckoutExecution | null): string | null {
  if (
    execution?.status !== 'requires_action' ||
    execution.presentation?.flow !== 'stripe_elements' ||
    execution.presentation.version !== 2
  ) {
    return null
  }
  return execution.presentation.payload.client_secret.trim() || null
}

export const StripeElementsPresentationRunner = forwardRef<
  CheckoutPresentationRunnerHandle,
  CheckoutPresentationRunnerProps
>(function StripeElementsPresentationRunner(
  {
    execution,
    method,
    methodPresentation,
    returnUrl,
    isStartingExecution,
    canStartExecution,
    startExecution,
    onOutcome,
    onValidityChange,
  },
  ref
) {
  const sectionRef = useRef<StripeExpressCheckoutSectionHandle>(null)
  const interactionGenerationRef = useRef(0)
  const handledNextActionRef = useRef<string | null>(null)
  const [sdkReady, setSdkReady] = useState(false)
  const [requestedNextAction, setRequestedNextAction] = useState<{
    clientSecret: string
    attempt: number
  } | null>(null)
  const [failedNextActionKey, setFailedNextActionKey] = useState<string | null>(null)
  const { t } = useTranslation()
  const methodReference = method.method_reference

  if (methodPresentation?.flow !== 'stripe_elements') {
    throw new Error('Stripe Elements method presentation is required')
  }

  useImperativeHandle(
    ref,
    () => ({
      submit: () => Promise.reject(new Error('Stripe wallet owns its submit interaction')),
    }),
    []
  )

  useEffect(() => {
    interactionGenerationRef.current += 1
    handledNextActionRef.current = null
    setRequestedNextAction(null)
    setFailedNextActionKey(null)
    return () => {
      interactionGenerationRef.current += 1
    }
  }, [methodReference])

  const reportFailure = useCallback(
    (error: StripeExpressCheckoutError) => {
      onOutcome(stripeFailureOutcome(method.pm_id, error))
    },
    [method.pm_id, onOutcome]
  )

  const handleConfirmationToken = useCallback(
    async (confirmationToken: string) => {
      const generation = interactionGenerationRef.current
      const targetExecution = await startExecution(methodReference, {
        confirmation_token: confirmationToken,
      })
      if (generation !== interactionGenerationRef.current) {
        return
      }
      if (!targetExecution) {
        onOutcome({
          status: 'cancelled',
          provider: 'stripe',
          pmId: method.pm_id,
          reason: 'user_cancelled',
        })
        return
      }
      if (['failed', 'canceled', 'expired'].includes(targetExecution.status)) {
        throw new StripeExpressCheckoutError(
          'Stripe wallet payment failed',
          'STRIPE_EXECUTION_FAILED',
          { execution_no: targetExecution.execution_no }
        )
      }
      if (targetExecution.status === 'requires_action') {
        const targetClientSecret = nextActionClientSecret(targetExecution)
        if (!targetClientSecret) {
          throw new StripeExpressCheckoutError(
            'Stripe wallet authentication is unavailable',
            'STRIPE_NEXT_ACTION_UNAVAILABLE',
            { execution_no: targetExecution.execution_no }
          )
        }
        setRequestedNextAction({ clientSecret: targetClientSecret, attempt: 0 })
        return
      }
      onOutcome({
        status: 'processing',
        provider: 'stripe',
        pmId: method.pm_id,
        raw: { execution_no: targetExecution.execution_no },
      })
    },
    [method.pm_id, methodReference, onOutcome, startExecution]
  )

  const clientSecret = nextActionClientSecret(execution)
  const nextActionAttempt =
    clientSecret && requestedNextAction?.clientSecret === clientSecret
      ? requestedNextAction.attempt
      : null
  const nextActionKey =
    clientSecret && nextActionAttempt !== null ? `${clientSecret}:${nextActionAttempt}` : null

  useEffect(() => {
    if (
      !clientSecret ||
      !nextActionKey ||
      !sdkReady ||
      handledNextActionRef.current === nextActionKey
    ) {
      return
    }
    const section = sectionRef.current
    if (!section) {
      return
    }
    const generation = interactionGenerationRef.current
    handledNextActionRef.current = nextActionKey
    setFailedNextActionKey(null)
    void section
      .handleNextAction(clientSecret)
      .then((result) => {
        if (generation !== interactionGenerationRef.current || !execution) {
          return
        }
        onOutcome({
          status: 'processing',
          provider: 'stripe',
          pmId: method.pm_id,
          raw: { execution_no: execution.execution_no, stripe_result: result },
        })
      })
      .catch((error: unknown) => {
        if (generation !== interactionGenerationRef.current) {
          return
        }
        setFailedNextActionKey(nextActionKey)
        reportFailure(
          error instanceof StripeExpressCheckoutError
            ? error
            : new StripeExpressCheckoutError(
                error instanceof Error ? error.message : 'Stripe wallet authentication failed',
                'STRIPE_NEXT_ACTION_FAILED',
                error
              )
        )
      })
  }, [clientSecret, execution, method.pm_id, nextActionKey, onOutcome, reportFailure, sdkReady])

  return (
    <>
      <StripeExpressCheckoutSection
        ref={sectionRef}
        presentation={methodPresentation}
        pmId={method.pm_id}
        interactionKey={method.method_binding}
        returnUrl={returnUrl}
        isPreparing={isStartingExecution || Boolean(clientSecret)}
        onBeforeConfirm={() => canStartExecution(methodReference)}
        onConfirmationToken={handleConfirmationToken}
        onFailure={reportFailure}
        onCancel={() =>
          onOutcome({
            status: 'cancelled',
            provider: 'stripe',
            pmId: method.pm_id,
            reason: 'wallet_closed',
          })
        }
        onSdkReadyChange={setSdkReady}
        onValidityChange={onValidityChange}
      />
      {clientSecret && (!nextActionKey || failedNextActionKey === nextActionKey) && (
        <Button
          type="button"
          variant="secondary"
          isFullWidth
          onClick={() =>
            setRequestedNextAction((request) => ({
              clientSecret,
              attempt:
                request?.clientSecret === clientSecret && failedNextActionKey === nextActionKey
                  ? request.attempt + 1
                  : 0,
            }))
          }
        >
          {failedNextActionKey === nextActionKey
            ? t('modal.paymentTimeout.tryAgain')
            : t('common.continue')}
        </Button>
      )}
    </>
  )
})
